import { motion } from "framer-motion";

const committees = [
  {
    tag: "Audit",
    title: "Audit Committee",
    body:
      "Rigorous review of internal financial controls, capital work-in-progress, project cost overruns and impairment triggers on long-cycle assets.",
    signals: ["CWIP Governance", "Cost Overrun Diagnostics", "Vendor Contract Trueing"],
  },
  {
    tag: "Risk",
    title: "Risk Management Committee",
    body:
      "Enterprise risk with a project-realism lens — schedule risk, statutory exposure, single-vendor dependency and force-majeure planning.",
    signals: ["Constraint Mapping", "Statutory & Regulatory Watch", "Interface Risk Resolution"],
  },
  {
    tag: "HSE / CSR",
    title: "HSE, Safety & CSR",
    body:
      "PSSR, RTO, incident-close-out and site-safety culture at plants and mining leases — including community and land-interface governance.",
    signals: ["PSSR & RTO", "Contractor Safety Assurance", "Community Interface"],
  },
  {
    tag: "Nomination",
    title: "Nomination & Governance",
    body:
      "Boardroom composition, succession maps for CAPEX and manufacturing leadership, and independent challenge to promoter-side proposals.",
    signals: ["Succession for CAPEX Leadership", "Independent Challenge Culture", "Board Skills Matrix"],
  },
  {
    tag: "CapEx",
    title: "CapEx & Strategy Committee",
    body:
      "Front-end loading, stage-gate readiness, IRR sensitivity and post-completion audits — the committee where two decades of practice directly compounds.",
    signals: ["Front-End Loading (FFL)", "Stage-Gate Readiness", "Post-Completion Audit"],
  },
];

const row = {
  hidden: { opacity: 0, y: 30 },
  visible: (i) => ({
    opacity: 1, y: 0,
    transition: { duration: 0.9, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function CommitteeFit() {
  return (
    <section id="committee-fit" className="relative border-b border-surface-2">
      <div className="mx-auto max-w-[1440px] px-6 md:px-16 py-28 md:py-40">
        <div className="grid grid-cols-12 gap-6 items-end mb-16">
          <div className="col-span-12 md:col-span-7">
            <div className="text-[11px] uppercase tracking-[0.28em] text-brass">
              <span className="tabular">05 —</span><span className="ml-3">Committee Fit</span>
            </div>
            <h2 className="mt-4 font-serif-display font-light text-ivory text-5xl md:text-6xl lg:text-7xl leading-[0.95] tracking-[-0.02em]">
              Where the value<br />
              <span className="italic text-brass">compounds at the board table.</span>
            </h2>
          </div>
          <div className="col-span-12 md:col-span-4 md:col-start-9 text-mutedfg text-sm md:text-base font-light leading-relaxed">
            A structured view of the five committees where an experienced capital-project independent adds
            technical realism to fiduciary duty — not the other way around.
          </div>
        </div>

        <div className="border-t border-surface-2">
          {committees.map((c, i) => (
            <motion.div
              key={c.tag}
              custom={i}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-60px" }}
              variants={row}
              className="grid grid-cols-12 gap-6 py-10 md:py-12 border-b border-surface-2 group"
            >
              <div className="col-span-3 md:col-span-2 tabular text-[10px] uppercase tracking-[0.28em] text-brass">
                0{i + 1} · {c.tag}
              </div>
              <div className="col-span-9 md:col-span-5">
                <h3 className="font-serif-display text-ivory text-2xl md:text-3xl lg:text-4xl font-light leading-[1.05] tracking-[-0.01em] transition-colors duration-500 group-hover:text-brass">
                  {c.title}
                </h3>
                <p className="mt-4 text-mutedfg text-base md:text-lg leading-relaxed font-light max-w-2xl">
                  {c.body}
                </p>
              </div>
              <div className="col-span-12 md:col-span-5 md:col-start-8">
                <div className="text-[10px] uppercase tracking-[0.28em] text-brass mb-4">Signature signals</div>
                <ul className="space-y-3">
                  {c.signals.map((s) => (
                    <li key={s} className="flex items-center gap-3 text-ivory font-light">
                      <span className="h-px w-6 bg-brass shrink-0" />
                      <span>{s}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
